export declare namespace AccountPageTokenSections {
	interface IdentityHeader {
		avatar: {
			fontWeight: string;
		};
		name: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
			mobileFontSize: string;
		};
		meta: {
			color: string;
			fontSize: string;
			lineHeight: string;
		};
		chip: {
			fontSize: string;
			fontWeight: string;
			padding: string;
		};
	}

	interface Separator {
		size: string;
		gap: string;
	}

	interface CardHeader {
		title: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		subtitle: {
			color: string;
			fontSize: string;
			lineHeight: string;
		};
	}

	interface PreferenceRow {
		padding: string;
		borderColor: string;
		label: {
			color: string;
			fontWeight: string;
		};
		description: {
			color: string;
			fontSize: string;
			lineHeight: string;
		};
	}

	interface SignaturePreview {
		background: string;
		borderColor: string;
		borderRadius: string;
		padding: string;
		lineColor: string;
		image: {
			maxWidth: string;
		};
		caption: {
			color: string;
			fontSize: string;
			lineHeight: string;
			paddingTop: string;
		};
	}

	interface SignatureEmpty {
		borderColor: string;
		borderRadius: string;
		padding: string;
		icon: {
			size: string;
			background: string;
			color: string;
			fontSize: string;
		};
		title: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		description: {
			color: string;
			fontSize: string;
			lineHeight: string;
		};
	}

	interface NavSignOut {
		padding: string;
		borderRadius: string;
		color: string;
		fontSize: string;
		lineHeight: string;
		fontWeight: string;
		iconSize: string;
		hoverBackground: string;
	}

	interface WorkloadTile {
		mobilePadding: string;
		background: string;
		borderColor: string;
		borderRadius: string;
		padding: string;
		hoverBorderColor: string;
		hoverShadow: string;
		icon: {
			size: string;
			borderRadius: string;
			background: string;
			color: string;
			fontSize: string;
		};
		count: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		label: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		chevron: {
			color: string;
			fontSize: string;
			hoverColor: string;
			hoverShift: string;
		};
		empty: {
			countColor: string;
			iconBackground: string;
			iconColor: string;
		};
	}
}

export type AccountPageTokens = {
	identityHeader: AccountPageTokenSections.IdentityHeader;
	separator: AccountPageTokenSections.Separator;
	cardHeader: AccountPageTokenSections.CardHeader;
	workloadTile: AccountPageTokenSections.WorkloadTile;
	preferenceRow: AccountPageTokenSections.PreferenceRow;
	signaturePreview: AccountPageTokenSections.SignaturePreview;
	signatureEmpty: AccountPageTokenSections.SignatureEmpty;
	navSignOut: AccountPageTokenSections.NavSignOut;
};

export const identityHeader: AccountPageTokenSections.IdentityHeader = {
	avatar: {
		fontWeight: '700',
	},
	name: {
		color: '{text.color}',
		fontSize: '1.75rem',
		lineHeight: '2.125rem',
		fontWeight: '700',
		mobileFontSize: '1.25rem',
	},
	meta: {
		color: '{surface.400}',
		fontSize: '0.9375rem',
		lineHeight: '1.375rem',
	},
	chip: {
		fontSize: '0.8125rem',
		fontWeight: '600',
		padding: '0.25rem 0.625rem',
	},
};

export const separator: AccountPageTokenSections.Separator = {
	size: '0.25rem',
	gap: '0.5rem',
};

export const cardHeader: AccountPageTokenSections.CardHeader = {
	title: {
		color: '{text.color}',
		fontSize: '{app.lg.font.size}',
		lineHeight: '{app.lg.line.height}',
		fontWeight: '700',
	},
	subtitle: {
		color: '{surface.400}',
		fontSize: '{app.sm.font.size}',
		lineHeight: '{app.sm.line.height}',
	},
};

export const workloadTile: AccountPageTokenSections.WorkloadTile = {
	mobilePadding: '0.75rem 0.625rem',
	background: '{content.background}',
	borderColor: '{surface.100}',
	borderRadius: '0.625rem',
	padding: '0.875rem 0.75rem',
	hoverBorderColor: '{primary.200}',
	hoverShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
	icon: {
		size: '2.25rem',
		borderRadius: '{border.radius.lg}',
		background: '{highlight.background}',
		color: '{primary.color}',
		fontSize: '1.125rem',
	},
	count: {
		color: '{text.color}',
		fontSize: '1.75rem',
		lineHeight: '2rem',
		fontWeight: '700',
	},
	label: {
		color: '{surface.400}',
		fontSize: '{app.sm.font.size}',
		lineHeight: '{app.sm.line.height}',
		fontWeight: '500',
	},
	chevron: {
		color: '{text.muted.color}',
		fontSize: '0.875rem',
		hoverColor: '{primary.color}',
		hoverShift: '0.125rem',
	},
	empty: {
		countColor: '{surface.400}',
		iconBackground: '{surface.50}',
		iconColor: '{text.muted.color}',
	},
};

export const preferenceRow: AccountPageTokenSections.PreferenceRow = {
	padding: '1.125rem 0',
	borderColor: '{surface.100}',
	label: {
		color: '{text.color}',
		fontWeight: '600',
	},
	description: {
		color: '{surface.400}',
		fontSize: '{app.sm.font.size}',
		lineHeight: '{app.sm.line.height}',
	},
};

export const signaturePreview: AccountPageTokenSections.SignaturePreview = {
	background: '{content.subtle.background}',
	borderColor: '{surface.100}',
	borderRadius: '0.625rem',
	padding: '2rem 1.5rem 1.5rem',
	lineColor: '{surface.200}',
	image: {
		maxWidth: '300px',
	},
	caption: {
		color: '{surface.400}',
		fontSize: '0.8125rem',
		lineHeight: '1.25rem',
		paddingTop: '0.375rem',
	},
};

export const signatureEmpty: AccountPageTokenSections.SignatureEmpty = {
	borderColor: '{surface.200}',
	borderRadius: '0.625rem',
	padding: '2.5rem 1.5rem',
	icon: {
		size: '3rem',
		background: '{highlight.background}',
		color: '{primary.color}',
		fontSize: '1.375rem',
	},
	title: {
		color: '{text.color}',
		fontSize: '1rem',
		lineHeight: '1.5rem',
		fontWeight: '700',
	},
	description: {
		color: '{surface.400}',
		fontSize: '{app.sm.font.size}',
		lineHeight: '{app.sm.line.height}',
	},
};

/** The sign-out item below the section links: the account menu's sign-out colours on the section links' box. */
export const navSignOut: AccountPageTokenSections.NavSignOut = {
	padding: '{appRoutedTabsList.vertical.item.padding}',
	borderRadius: '{appRoutedTabsList.vertical.item.borderRadius}',
	color: '{userInfo.signOut.color}',
	fontSize: '{userInfo.item.fontSize}',
	lineHeight: '{userInfo.item.lineHeight}',
	fontWeight: '{userInfo.signOut.fontWeight}',
	iconSize: '{userInfo.item.iconSize}',
	hoverBackground: '{userInfo.signOut.hoverBackground}',
};

export default {
	identityHeader,
	separator,
	cardHeader,
	workloadTile,
	preferenceRow,
	signaturePreview,
	signatureEmpty,
	navSignOut,
} satisfies AccountPageTokens;
